// panels/PanelWrapper.jsx
import React, { useState, useRef, useEffect } from 'react';
import { MoreHorizontal, Download, Eye, Link2, RefreshCw } from 'lucide-react';

export function PanelWrapper({ title, subtitle, datasource = 'MongoDB', loading, children, onExport, className = '' }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleCopyLink() {
    navigator.clipboard?.writeText(window.location.href);
    setMenuOpen(false);
  }

  return (
    <div
      className={className}
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px 8px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-tertiary)',
          flexShrink: 0,
        }}
      >
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', lineHeight: 1.3 }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>{subtitle}</div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {datasource && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase',
              color: 'var(--text-3)', background: 'rgba(255,255,255,0.04)',
              border: '1px solid var(--border)', borderRadius: 4, padding: '2px 6px',
            }}>
              {datasource}
            </span>
          )}

          {loading && (
            <RefreshCw size={11} style={{ color: 'var(--text-3)', animation: 'spin 1s linear infinite' }} />
          )}

          <div style={{ position: 'relative' }} ref={menuRef}>
            <button
              onClick={() => setMenuOpen(o => !o)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-3)', padding: '2px 4px', borderRadius: 4,
                display: 'flex', alignItems: 'center',
              }}
              className="hover:text-white"
            >
              <MoreHorizontal size={14} />
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: '100%', zIndex: 200,
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 8, padding: '4px 0', minWidth: 150,
                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              }}>
                {[
                  { icon: Eye,      label: 'Inspect',    action: () => setMenuOpen(false) },
                  { icon: Download, label: 'Export CSV',  action: () => { onExport?.(); setMenuOpen(false); } },
                  { icon: Link2,    label: 'Copy link',   action: handleCopyLink },
                ].map(({ icon: Icon, label, action }) => (
                  <button key={label} onClick={action}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '7px 14px', background: 'none',
                      border: 'none', cursor: 'pointer', fontSize: 12,
                      color: 'var(--text-2)', textAlign: 'left',
                    }}
                    className="hover:bg-white/5">
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(10,14,26,0.6)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div className="thinking-dot" /><div className="thinking-dot" /><div className="thinking-dot" />
          </div>
        )}
        {children}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
